// ABOUTME: SPEC-82 §4.2 — the intro-film skip and the tap-to-talk hold as ONE composed unit, so App.js
// ABOUTME: only reads toggles and calls this hook (D-4); every flag-off path returns today's values.

import { useCallback, useRef } from 'react';
import useTapGate from './useTapGate';
import useFilmRecord from './useFilmRecord';
import useLanding from './useLanding';
import useFirstVisitFilm, { isFirstVisitFilm } from './useFirstVisitFilm';

/**
 * @param {object} p
 * @param {Readonly<Record<string, true>>} p.ux the readUiExperiment() map
 * @param {boolean} p.reelMode this tenant is `reel_mode`
 * @param {boolean} p.hasSeenIntroBefore the read-once visit fact (App's useVisitMemory)
 * @param {boolean} p.showIntro the film-on-screen state
 * @param {{current: boolean}} p.showIntroRef the ONE film gate (mic/VAD/agent audio/intro_playing)
 * @param {Function} p.sendIntroFinished App's single funnel, resolved at call time
 * @param {Function} [p.isSocketOpen] whether the agent socket is open right now (a log value only)
 * @param {boolean} [p.started] App's isExperienceStarted (no_enter_gate: the landing CTA waits until it is)
 * @param {Function} [p.enter] App's requestEnter, resolved at call time (no_enter_gate: the CTA's tap)
 * @param {Function} [p.setShowIntro] App's film-on-screen setter (first_visit_film puts the film up before entry)
 */
export default function useIntroExperiment({
  ux, reelMode, hasSeenIntroBefore, showIntro, showIntroRef, sendIntroFinished, isSocketOpen, started = false, enter, setShowIntro,
}) {
  // first_visit_film: the film plays BEFORE entry, so entry itself has no film to wait for (skipFilm) while the Projectionist still
  // boots on it (skipToHome off). Flag-off both are today's skip_intro_film fact.
  const filmFirst = isFirstVisitFilm({ ux, reelMode, hasSeenIntroBefore });
  const skipFilm = !!(ux.skip_intro_film && reelMode && !hasSeenIntroBefore) || filmFirst;
  const skipToHome = skipFilm && !filmFirst;
  useFirstVisitFilm({ filmFirst, started, setShowIntro });
  const landing = useLanding({ ux, reelMode, skipFilm, hasSeenIntroBefore, started, enter, release: sendIntroFinished });
  const gate = useTapGate({
    ux, reelMode, hasSeenIntroBefore, showIntroRef, sendIntroFinished, isSocketOpen, landed: landing.engaged,
  });
  const film = useFilmRecord({ ux, showIntro });
  const { isWaiting, tap: landingTap } = landing;
  const { markSeen: gateMarkSeen, tap: gateTap } = gate;

  // Before the landing tap the visit record waits, exactly as it does under a held gate; the tap itself writes it.
  const markSeen = useCallback(() => { if (!isWaiting()) gateMarkSeen(); }, [isWaiting, gateMarkSeen]);
  // first_visit_film: no entry while the film is up (the landing follows it). The STATE, not showIntroRef: App raises that ref for
  // the whole landing hold too.
  const filmUpRef = useRef(showIntro);
  filmUpRef.current = showIntro;
  const tap = useCallback(() => {
    if (!isWaiting()) return gateTap();
    if (filmFirst && filmUpRef.current) return undefined;
    return landingTap();
  }, [isWaiting, landingTap, gateTap, filmFirst]);
  // The welcome trigger is sent: the landing's held requests (a label, a message) go right behind it.
  const { afterIntroFinishedSent: filmAfterSent } = film;
  const { flushHeld } = landing;
  const afterIntroFinishedSent = useCallback(() => { filmAfterSent(); flushHeld(); }, [filmAfterSent, flushHeld]);

  // `holding` drives the dock (CTA instead of dots, no revealed row): the gate, or a FIRST-TIME landing. A returning guest lands
  // on the dots and controls and enters from the enable-microphone widget instead (App's VoiceOffPrompt).
  const ctaLanding = landing.waiting && !landing.returning;
  const holding = gate.holding || ctaLanding;
  return {
    skipFilm, skipToHome, filmFirst, holding, isHolding: gate.isHolding,
    ctaVisible: (gate.holding && !showIntro) || (ctaLanding && !showIntro), // showIntro is only ever up pre-entry under first_visit_film
    arm: gate.arm, tap, markSeen, landing,
    frameExtras: film.frameExtras, afterIntroFinishedSent,
  };
}
